import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  alpha,
  useTheme,
  Divider,
} from '@mui/material';
import {
  Add,
  Delete,
  LocalShipping,
  Inventory,
  Factory,
  Store,
  Upload,
  Download,
} from '@mui/icons-material';

interface SupplyChainMappingStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  updateData: (data: any) => void;
}

interface Supplier {
  id: string;
  name: string;
  location: string;
  products: string;
  leadTime: string;
}

interface Product {
  id: string;
  name: string;
  category: string;
  sku: string;
}

interface Location {
  id: string;
  name: string;
  type: string;
  address: string;
}

const SupplyChainMappingStep: React.FC<SupplyChainMappingStepProps> = ({
  onNext,
  data,
  updateData,
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  
  const [suppliers, setSuppliers] = useState<Supplier[]>(data.suppliers || []);
  const [products, setProducts] = useState<Product[]>(data.products || []);
  const [locations, setLocations] = useState<Location[]>(data.locations || []);

  const [newSupplier, setNewSupplier] = useState<Partial<Supplier>>({});
  const [newProduct, setNewProduct] = useState<Partial<Product>>({});
  const [newLocation, setNewLocation] = useState<Partial<Location>>({});

  // Suppliers
  const addSupplier = () => {
    if (newSupplier.name && newSupplier.location) {
      const supplier: Supplier = {
        id: Date.now().toString(),
        name: newSupplier.name,
        location: newSupplier.location,
        products: newSupplier.products || '',
        leadTime: newSupplier.leadTime || '',
      };
      const updated = [...suppliers, supplier];
      setSuppliers(updated);
      updateData({ ...data, suppliers: updated });
      setNewSupplier({});
    }
  };

  const removeSupplier = (id: string) => {
    const updated = suppliers.filter((s) => s.id !== id);
    setSuppliers(updated);
    updateData({ ...data, suppliers: updated });
  };

  // Products
  const addProduct = () => {
    if (newProduct.name && newProduct.category) {
      const product: Product = {
        id: Date.now().toString(),
        name: newProduct.name,
        category: newProduct.category,
        sku: newProduct.sku || '',
      };
      const updated = [...products, product];
      setProducts(updated);
      updateData({ ...data, products: updated });
      setNewProduct({});
    }
  };

  const removeProduct = (id: string) => {
    const updated = products.filter((p) => p.id !== id);
    setProducts(updated);
    updateData({ ...data, products: updated });
  };

  // Locations
  const addLocation = () => {
    if (newLocation.name && newLocation.type) {
      const location: Location = {
        id: Date.now().toString(),
        name: newLocation.name,
        type: newLocation.type,
        address: newLocation.address || '',
      };
      const updated = [...locations, location];
      setLocations(updated);
      updateData({ ...data, locations: updated });
      setNewLocation({});
    }
  };

  const removeLocation = (id: string) => {
    const updated = locations.filter((l) => l.id !== id);
    setLocations(updated);
    updateData({ ...data, locations: updated });
  };

  const handleNext = () => {
    onNext({ suppliers, products, locations });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            mx: 'auto',
            mb: 2,
          }}
        >
          <LocalShipping sx={{ fontSize: 32 }} />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Map Your Supply Chain
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Add your suppliers, products, and locations to build your network
        </Typography>
      </Box>

      {/* Quick Actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<Upload />}
          size="small"
        >
          Import CSV
        </Button>
        <Button
          variant="outlined"
          startIcon={<Download />}
          size="small"
        >
          Download Template
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          centered
        >
          <Tab
            icon={<LocalShipping />}
            label={`Suppliers (${suppliers.length})`}
            iconPosition="start"
          />
          <Tab
            icon={<Inventory />}
            label={`Products (${products.length})`}
            iconPosition="start"
          />
          <Tab
            icon={<Factory />}
            label={`Locations (${locations.length})`}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Suppliers Tab */}
      {activeTab === 0 && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Add Supplier
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Supplier Name"
                    value={newSupplier.name || ''}
                    onChange={(e) =>
                      setNewSupplier({ ...newSupplier, name: e.target.value })
                    }
                    placeholder="e.g., TechComponents Inc"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Location"
                    value={newSupplier.location || ''}
                    onChange={(e) =>
                      setNewSupplier({ ...newSupplier, location: e.target.value })
                    }
                    placeholder="e.g., Shenzhen, China"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Products Supplied"
                    value={newSupplier.products || ''}
                    onChange={(e) =>
                      setNewSupplier({ ...newSupplier, products: e.target.value })
                    }
                    placeholder="e.g., Electronic components"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Lead Time"
                    value={newSupplier.leadTime || ''}
                    onChange={(e) =>
                      setNewSupplier({ ...newSupplier, leadTime: e.target.value })
                    }
                    placeholder="e.g., 4-6 weeks"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={addSupplier}
                    disabled={!newSupplier.name || !newSupplier.location}
                  >
                    Add Supplier
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {suppliers.length > 0 ? (
            <List>
              {suppliers.map((supplier) => (
                <Card key={supplier.id} sx={{ mb: 2 }}>
                  <ListItem>
                    <ListItemText
                      primary={supplier.name}
                      secondary={
                        <Box>
                          <Typography variant="body2" component="span">
                            📍 {supplier.location}
                          </Typography>
                          {supplier.products && (
                            <>
                              <br />
                              <Typography variant="body2" component="span">
                                📦 {supplier.products}
                              </Typography>
                            </>
                          )}
                          {supplier.leadTime && (
                            <>
                              <br />
                              <Typography variant="body2" component="span">
                                ⏱️ {supplier.leadTime}
                              </Typography>
                            </>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => removeSupplier(supplier.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Card>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              No suppliers added yet. Add your first supplier to get started!
            </Alert>
          )}
        </Box>
      )}

      {/* Products Tab */}
      {activeTab === 1 && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Add Product
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Product Name"
                    value={newProduct.name || ''}
                    onChange={(e) =>
                      setNewProduct({ ...newProduct, name: e.target.value })
                    }
                    placeholder="e.g., Laptop Model X"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Category"
                    value={newProduct.category || ''}
                    onChange={(e) =>
                      setNewProduct({ ...newProduct, category: e.target.value })
                    }
                    placeholder="e.g., Electronics"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="SKU (Optional)"
                    value={newProduct.sku || ''}
                    onChange={(e) =>
                      setNewProduct({ ...newProduct, sku: e.target.value })
                    }
                    placeholder="e.g., LAP-X-001"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={addProduct}
                    disabled={!newProduct.name || !newProduct.category}
                  >
                    Add Product
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {products.length > 0 ? (
            <List>
              {products.map((product) => (
                <Card key={product.id} sx={{ mb: 2 }}>
                  <ListItem>
                    <ListItemText
                      primary={product.name}
                      secondary={
                        <Box>
                          <Chip label={product.category} size="small" sx={{ mr: 1 }} />
                          {product.sku && (
                            <Typography variant="caption" component="span">
                              SKU: {product.sku}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => removeProduct(product.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Card>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              No products added yet. Add your first product to get started!
            </Alert>
          )}
        </Box>
      )}

      {/* Locations Tab */}
      {activeTab === 2 && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Add Location
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Location Name"
                    value={newLocation.name || ''}
                    onChange={(e) =>
                      setNewLocation({ ...newLocation, name: e.target.value })
                    }
                    placeholder="e.g., New York Warehouse"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Type"
                    value={newLocation.type || ''}
                    onChange={(e) =>
                      setNewLocation({ ...newLocation, type: e.target.value })
                    }
                    placeholder="e.g., Warehouse, Factory, Store"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Address (Optional)"
                    value={newLocation.address || ''}
                    onChange={(e) =>
                      setNewLocation({ ...newLocation, address: e.target.value })
                    }
                    placeholder="e.g., New York, US"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={addLocation}
                    disabled={!newLocation.name || !newLocation.type}
                  >
                    Add Location
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {locations.length > 0 ? (
            <List>
              {locations.map((location) => (
                <Card key={location.id} sx={{ mb: 2 }}>
                  <ListItem>
                    <ListItemText
                      primary={location.name}
                      secondary={
                        <Box>
                          <Chip label={location.type} size="small" sx={{ mr: 1 }} />
                          {location.address && (
                            <Typography variant="caption" component="span">
                              {location.address}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => removeLocation(location.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Card>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              No locations added yet. Add your first location to get started!
            </Alert>
          )}
        </Box>
      )}

      {/* Summary */}
      <Card sx={{ mt: 3, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Supply Chain Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="h4" color="primary">
                {suppliers.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Suppliers
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="h4" color="primary">
                {products.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Products
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="h4" color="primary">
                {locations.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Locations
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          💡 <strong>Tip:</strong> You can add more details later. For now, just add
          the key elements of your supply chain. You can always import bulk data or
          add more items from your dashboard.
        </Typography>
      </Alert>

      {/* Hidden submit */}
      <Box sx={{ display: 'none' }}>
        <button onClick={handleNext}>Submit</button>
      </Box>
    </Box>
  );
};

export default SupplyChainMappingStep;
